<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';

  interface Props {
    open: boolean;
    onclose: () => void;
  }

  let { open, onclose }: Props = $props();
</script>

<BottomSheet {open} title="ヘルプ" {onclose}>
  <section class="block">
    <h3>基本操作</h3>
    <ul>
      <li>ホイールをタップ → スピン開始</li>
      <li>結果オーバーレイをタップ → 閉じる（次のスピンは改めてホイールをタップ）</li>
      <li>スピン中のホイールタップは無効</li>
      <li>マーカーをドラッグ → 任意角度に変更（マーカー位置「自由」）</li>
    </ul>
  </section>

  <section class="block">
    <h3>マルチルーレット (グリッド)</h3>
    <p>ルーレットが 2 件以上あるとグリッド表示。1 件の場合は自動的に詳細表示。</p>
    <ul>
      <li>各ホイールをタップ → 個別にスピン</li>
      <li>ヘッダー右の <b>▶ 同時実行</b> → 表示中のルーレット全てを同時スピン (結果表示中も再回転)</li>
      <li>ルーレット名をタップ → 詳細モードへ (項目編集等が可能)</li>
      <li>ヘッダー左の ← → グリッドへ戻る</li>
    </ul>
  </section>

  <section class="block">
    <h3>ヘッダー (≡ ハンバーガー)</h3>
    <p>ルーレット管理画面が開きます。</p>
    <ul>
      <li>ルーレット切替・追加 (＋)・改名 (✎)・複製 (⎘)・削除 (🗑)</li>
      <li>👁 グリッド表示 ON/OFF (1 件は必ず表示)</li>
      <li>下部リンク: 履歴 / アプリ設定 / ヘルプ</li>
    </ul>
  </section>

  <section class="block">
    <h3>項目リストのアイコン</h3>
    <dl class="legend">
      <div><dt><span class="ic">🔀</span><span class="ttl">並替</span></dt><dd>今すぐシャッフル。項目本体は変更せず表示順だけ変える。色は維持される。</dd></div>
      <div><dt><span class="ic">↩</span><span class="ttl">戻す</span></dt><dd>シャッフルを取り消し元の順序に戻す。シャッフル中のみ表示。</dd></div>
      <div><dt><span class="ic">🔁</span><span class="ttl">毎回</span></dt><dd>スピンごとに自動シャッフル。アクティブ時はラベルがアクセント色に。</dd></div>
      <div><dt><span class="ic">⇅</span><span class="ttl">移動</span></dt><dd>並び替えモード。項目を 1 つずつ上下に移動できる。</dd></div>
      <div><dt><span class="ic">編集</span></dt><dd>1 行 1 項目のテキスト編集。既存の ON/OFF・確率設定は同じテキストの項目に引き継がれる。</dd></div>
      <div><dt><span class="ic">⋮</span></dt><dd>項目ごとの詳細設定（確率モード・分割数・カスタム色）を開く。</dd></div>
    </dl>
  </section>

  <section class="block">
    <h3>確率モード</h3>
    <ul>
      <li><b>デフォルト</b>: 他のデフォルト項目と均等配分</li>
      <li><b>固定 %</b>: 指定の割合を確保（0% &lt; v &lt; 100、合計 100% を超える設定は不可）</li>
      <li><b>重み</b>: 他項目との比率（0.25 〜）。大きいほど当選しやすい</li>
    </ul>
  </section>

  <section class="block">
    <h3>分割 / カスタム色</h3>
    <p>項目詳細 (⋮) で各項目を最大 10 セグメントに分割できます。シャッフル時の挙動は「アプリ設定 → ランダム時に分割を均等維持」で切替可能。</p>
    <p>同じく項目詳細でカスタム色を設定可能。色はシャッフルしても項目に紐付いて変わりません。</p>
  </section>

  <section class="block">
    <h3>連続実行 (被りなし)</h3>
    <p>下部操作バーの「連続実行 ▶」から、回数と保持秒を指定して被りなしで N 回スピン。実行中は上部に進捗 (中断ボタン付)。完了後に結果一覧表示。</p>
  </section>

  <section class="block">
    <h3>セット</h3>
    <p>下部の操作バーのセット名 ▼ をタップ → セット切替・追加・改名・削除。各ルーレットは複数セットを持てます。</p>
  </section>

  <section class="block">
    <h3>履歴</h3>
    <p>≡ → 履歴 で当選履歴を確認 (各ルーレット 100 件まで)。全削除ボタンあり。</p>
  </section>

  <section class="block">
    <h3>ルーレット個別設定 (⚙)</h3>
    <p>右上の ⚙ から: スピン時間・回転方向・スピン音/決定音 (試聴あり)・文字の向き/サイズ/寄せ・マーカー位置/描画スタイル・セグメント並び方向。「初期値に戻す」で全項目を初期化できます。</p>
  </section>

  <section class="block">
    <h3>アプリ設定</h3>
    <p>≡ → アプリ設定 で全ルーレット共通の設定を変更できます:</p>
    <ul>
      <li><b>テーマ</b>: 自動 (OS) / ライト / ダーク</li>
      <li><b>音量</b>: 全ルーレット共通の出力音量</li>
      <li><b>項目リスト表示</b>: 確率 (%) / 当選回数の表示 ON/OFF</li>
      <li><b>ランダム時に分割を均等維持</b>: シャッフル時に分割を均等維持するか (OFF=Windows 同等、各 split 独立配置)</li>
      <li><b>データ</b>: 全データを JSON エクスポート / インポート</li>
    </ul>
  </section>

  <div class="actions">
    <button class="close" onclick={onclose}>閉じる</button>
  </div>
</BottomSheet>

<style>
  .block {
    margin-bottom: var(--space-lg);
  }

  .block h3 {
    margin: 0 0 var(--space-sm);
    font-size: var(--font-size-base);
    font-weight: 700;
    color: var(--color-accent);
    border-bottom: 1px solid var(--color-border);
    padding-bottom: 4px;
  }

  .block p,
  .block ul {
    margin: 0;
    font-size: var(--font-size-sm);
    line-height: 1.6;
    color: var(--color-fg);
  }

  .block ul {
    padding-left: 1.2em;
  }

  .legend {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    margin: 0;
  }

  .legend > div {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: var(--space-sm);
    align-items: start;
    font-size: var(--font-size-sm);
  }

  .legend dt {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
    color: var(--color-fg-muted);
    margin: 0;
  }

  .legend dt .ic {
    font-size: 16px;
    line-height: 1;
  }

  .legend dt .ttl {
    font-size: 11px;
  }

  .legend dd {
    margin: 0;
    line-height: 1.5;
  }

  .actions {
    margin-top: var(--space-lg);
  }

  .close {
    width: 100%;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
</style>
